import React, {useState, useRef, useEffect} from 'react';
import ReactDOM from 'react-dom';

import * as TableConstants from 'notebook/tables/TableConstants';
import Col from 'ui-library/bs/Col';
import FormGroup from 'ui-library/bs/FormGroup';
import Modal from 'ui-library/modals/Modal';
import {ElementMouseEvent} from 'util/Types';

export interface RenameTextBoxModalProps {
  fileId: string;
  initialName?: string;
  isNameUnique: (name: string) => boolean;
  setNameAndClose: (name: string | null) => void;
}

export default function RenameTextBoxModal(props: RenameTextBoxModalProps): JSX.Element {
  const [name, setName] = useState('');
  const [nameErrorMessage, setNameErrorMessage] = useState<string | null>('');
  const nameField = useRef(null);

  useEffect(() => {
    const name = props.initialName || '';

    setName(name);
    setNameErrorMessage(null);
  }, [props.initialName]);

  function isNameLong(name: string): boolean {
    return name.length > TableConstants.TABLE_NAME_MAX_CHARS;
  }

  function getNameErrorMessage(name: string): string | null {
    if (isNameLong(name)) {
      return `Name exceeds ${TableConstants.TABLE_NAME_MAX_CHARS} characters`;
    }
    if (!props.isNameUnique(name) && name !== props.initialName) {
      return 'Text Box name already in use';
    }
    return null;
  }

  function handleSubmit(): void {
    const nameErrorMessage = getNameErrorMessage(name);
    if (nameErrorMessage == null) {
      if (name === props.initialName) {
        // User did not change the name
        props.setNameAndClose(null);
      } else {
        props.setNameAndClose(name);
      }
    } else {
      setNameErrorMessage(nameErrorMessage);
    }
  }

  function handleClose(): void {
    // Use null to indicate the user cancelled; setNameAndClose still restores the selection
    props.setNameAndClose(null);
  }

  function focus(): void {
    const node = ReactDOM.findDOMNode(nameField.current) as HTMLInputElement;
    node.focus();
    node.select();
  }

  useEffect(() => {
    focus();
  }, []);

  useEffect(() => {
    focus();
  }, [nameErrorMessage]);

  return (
    <div
      onMouseDown={(e: ElementMouseEvent) => {
        e.stopPropagation();
      }}
    >
      <Modal
        title="Rename text box"
        onRequestClose={handleClose}
        onRequestSubmit={handleSubmit}
        buttons={[{text: 'Save', submit: true, disabled: name.length === 0}]}
      >
        <div className="form-horizontal">
          <FormGroup>
            <Col sm={12}>
              <label className="u-tiny-spacing-bottom">Text Box Name</label>
              {nameErrorMessage != null ? (
                <span className="u-danger-color u-tiny-spacing-left">{nameErrorMessage}</span>
              ) : null}
              <input
                type="text"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                }}
                className="form-control"
                ref={nameField}
              />
            </Col>
          </FormGroup>
        </div>
      </Modal>
    </div>
  );
}
